import { useRef, useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import mapboxgl from 'mapbox-gl';
import projectsData from "../data/projects.json";
import { slugify } from "../utils/slugify";

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;

export default function ProjectsPage() {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [activeFilter, setActiveFilter] = useState("all");
  const [filteredProjects, setFilteredProjects] = useState([]);

  const allProjects = useMemo(() => (
    projectsData.map(p => ({
      ...p,
      slug: slugify(p.name),
      lots: p.lots || p.homes,
      image: p.image || "/proj.jpg"
    }))
  ), []);

  useEffect(() => {
    const filtered = allProjects.filter(p => {
      const type = (p.type || "").toLowerCase();
      if (activeFilter === "residential") return type === "residential";
      if (activeFilter === "commercial") return type === "commercial" || type === "multifamily";
      return true;
    });
    setFilteredProjects(filtered);
  }, [activeFilter, allProjects]);

  useEffect(() => {
    if (!map.current && mapContainer.current) {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/dark-v11',
        center: [-79.8431, 34.8271],
        zoom: 6.5
      });
      map.current.addControl(new mapboxgl.NavigationControl(), 'top-left');
    }
  }, []);

  useEffect(() => {
    if (!map.current) return;
    const currentMap = map.current;

    const markers = currentMap.markers || [];
    markers.forEach(m => m.remove());
    currentMap.markers = [];

    filteredProjects.forEach((project) => {
      if (!project.lat || !project.lng) return;
      const el = document.createElement("div");
      el.className = "w-4 h-4 bg-blue-600 rounded-full border-2 border-white shadow-md";
      el.setAttribute("tabindex", "0");
      el.setAttribute("role", "button");
      el.setAttribute("aria-label", `View ${project.name} project details`);

      const marker = new mapboxgl.Marker({ element: el, anchor: "bottom" })
        .setLngLat([project.lng, project.lat])
        .setPopup(new mapboxgl.Popup({ offset: 25 }).setHTML(
          `<div class="text-center p-1">
            <h3 class="font-bold text-blue-900 text-base">${project.name}</h3>
            <p class="text-xs">${project.county} County, ${project.state}</p>
            <p class="text-xs">${project.lots ? project.lots.toLocaleString() : "-"} Lots</p>
          </div>`
        ))
        .addTo(currentMap);
      currentMap.markers.push(marker);
    });
  }, [filteredProjects]);

  return (
    <>
      <Helmet>
        <title>Projects | CovePoint Development</title>
        <meta name="description" content="Explore residential and commercial communities developed by CovePoint across the Southeast." />
        <meta property="og:title" content="Our Projects | CovePoint Development" />
        <meta property="og:description" content="Explore residential and commercial communities developed by CovePoint across the Southeast." />
        <meta property="og:image" content="https://covepoint.com/share.jpg" />
        <meta property="og:url" content="https://covepoint.com/projects" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://covepoint.com/share.jpg" />
        <link rel="canonical" href="https://covepoint.com/projects" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "CovePoint Project Directory",
            "url": "https://covepoint.com/projects",
            "description": "View our active and past residential and commercial real estate developments across the Southeast."
          })}
        </script>
      </Helmet>

      <main className="min-h-screen font-roboto text-gray-800">
        <div className="relative h-[60vh] sm:h-[70vh] md:h-[80vh] lg:h-[90vh] overflow-hidden text-white">
          <div className="absolute inset-0 bg-cover bg-center z-0 opacity-80" style={{ backgroundImage: "url('/hero-communities.jpg')" }} />
          <div className="absolute inset-0 bg-black/50 z-10" />
          <div className="relative z-20 flex flex-col items-center justify-center h-full text-center px-4 gap-6">
            <h1 className="text-4xl sm:text-5xl font-bold mb-2">Our Projects</h1>
            <div className="flex flex-col gap-3 mt-4">
              <button
                onClick={() => {
                  const el = document.getElementById("project-filter");
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="w-fit border-2 border-blue-500 text-white font-semibold text-sm px-4 py-2 rounded transition hover:bg-blue-600 bg-white/10 backdrop-blur-sm flex items-center gap-2"
              >
                Explore the Projects
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <div className="w-fit mx-auto">
                <button
                  onClick={() => {
                    const el = document.getElementById("project-map");
                    if (el) {
                      const rect = el.getBoundingClientRect();
                      const scrollY = window.pageYOffset + rect.top - (window.innerHeight * 0.1);
                      window.scrollTo({ top: scrollY, behavior: "smooth" });
                    }
                  }}
                  className="w-fit border-2 border-blue-500 text-white font-semibold text-sm px-4 py-2 rounded transition hover:bg-blue-600 bg-white/10 backdrop-blur-sm flex items-center gap-2"
                >
                  Map View
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div id="project-filter" className="..." />
        <div className="flex justify-center gap-4 py-6 flex-wrap" role="tablist" aria-label="Project type filters">
          {["all", "residential", "commercial"].map(type => (
            <button
              key={type}
              onClick={() => setActiveFilter(type)}
              className={`relative text-base sm:text-lg md:text-xl font-medium sm:font-semibold uppercase
                after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:translate-x-[-50%]
                after:h-[2px] after:bg-black after:w-0
                hover:after:w-full after:transition-all after:duration-300
                ${activeFilter === type ? "after:w-full" : ""}`}
              role="tab"
              aria-selected={activeFilter === type}
              aria-label={`Show ${type} projects`}
            >
              <span className="inline-block">{type}</span>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 max-w-7xl mx-auto px-4 py-6">
          {filteredProjects.map(project => (
            <a
              key={project.slug}
              href={`/projects/${project.slug}`}
              className="bg-white shadow-[0_6px_20px_rgba(0,0,0,0.25)] hover:shadow-inner hover:translate-y-1 active:translate-y-0 transition-transform duration-300 rounded-lg overflow-hidden border"
            >
              <div className="aspect-[4/3] bg-gray-100">
                <img
                  src={project.image}
                  alt={project.name}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-3 sm:p-4 text-center">
                <h3 className="text-base sm:text-lg font-semibold text-[#0B1D42]">{project.name}</h3>
                <p className="text-xs sm:text-sm text-gray-600">{project.county} County, {project.state}</p>
                {!["commercial", "multifamily"].includes(project.type?.toLowerCase()) && (
                  <p className="text-sm font-medium text-gray-800">
                    {project.lots ? project.lots.toLocaleString() : "-"} Lots
                  </p>
                )}
                <p className="text-xs text-gray-500 italic">{project.type}</p>
              </div>
            </a>
          ))}
        </div>

        <div id="project-map" className="..." />
        <section id="map" className="py-10 px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0B1D42] text-center mb-6 sm:mb-8">Project Map</h2>
          <div className="w-full max-w-6xl mx-auto h-[400px] sm:h-[450px] md:h-[500px] rounded-lg overflow-hidden shadow-xl bg-gray-100 border border-gray-200">
            <div ref={mapContainer} className="w-full h-full" />
          </div>
        </section>
      </main>
    </>
  );
}
